import React, { useState, useRef } from 'react';
import { PaperClipIcon, XMarkIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { customerTicketAttachmentService } from '../../services/customerTicketAttachmentService';
import { TicketAttachment } from '../../types/customerTicket';
import toast from 'react-hot-toast';

interface AttachmentUploaderProps {
  ticketId: string;
  ticketTitle: string;
  ticketType: string;
  customerId: string;
  customerName: string;
  organizationId: string;
  uploadedBy: string;
  uploadedByName: string;
  uploadedByType: 'customer' | 'staff';
  onAttachmentAdded: (attachment: TicketAttachment) => void;
  maxFiles?: number;
}

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  ticketId,
  ticketTitle,
  ticketType,
  customerId,
  customerName,
  organizationId,
  uploadedBy,
  uploadedByName,
  uploadedByType,
  onAttachmentAdded,
  maxFiles = 5
}) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length + selectedFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} fájl tölthető fel`);
      return;
    }

    // Validálás
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = customerTicketAttachmentService.validateFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      // Automatikus feltöltés
      setUploading(true);
      try {
        for (const file of validFiles) {
          const attachment = await customerTicketAttachmentService.uploadAttachment({
            file,
            ticketId,
            ticketTitle,
            ticketType,
            customerId,
            customerName,
            organizationId,
            uploadedBy,
            uploadedByName,
            uploadedByType
          });

          onAttachmentAdded(attachment);
        }

        toast.success(`${validFiles.length} fájl feltöltve`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        toast.error('Hiba a fájlok feltöltése során');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    
    if (files.length + selectedFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} fájl tölthető fel`);
      return;
    }

    // Validálás
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = customerTicketAttachmentService.validateFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      // Automatikus feltöltés
      setUploading(true);
      try {
        for (const file of validFiles) {
          const attachment = await customerTicketAttachmentService.uploadAttachment({
            file,
            ticketId,
            ticketTitle,
            ticketType,
            customerId,
            customerName,
            organizationId,
            uploadedBy,
            uploadedByName,
            uploadedByType
          });

          onAttachmentAdded(attachment);
        }

        toast.success(`${validFiles.length} fájl feltöltve`);
      } catch (error) {
        console.error('Error uploading files:', error);
        toast.error('Hiba a fájlok feltöltése során');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const attachment = await customerTicketAttachmentService.uploadAttachment({
          file,
          ticketId,
          ticketTitle,
          ticketType,
          customerId,
          customerName,
          organizationId,
          uploadedBy,
          uploadedByName,
          uploadedByType
        });

        onAttachmentAdded(attachment);
      }

      toast.success(`${selectedFiles.length} fájl feltöltve`);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Hiba a fájlok feltöltése során');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <PhotoIcon className="h-5 w-5 text-blue-500" />;
    }
    return <DocumentIcon className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="space-y-3">
      {/* Drag & Drop Area */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Mellékletek (opcionális)
        </label>
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
            isDragging
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
          
          <div className="text-center">
            <PaperClipIcon className={`mx-auto h-12 w-12 ${
              isDragging ? 'text-primary-500' : 'text-gray-400'
            }`} />
            <div className="mt-2">
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Kattints ide
              </label>
              <span className="text-sm text-gray-600 dark:text-gray-400"> vagy húzd ide a fájlokat</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Max {maxFiles} fájl, egyenként max 10 MB
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              PNG, JPG, PDF, Word, Excel, TXT
            </p>
          </div>
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Kiválasztott fájlok ({selectedFiles.length}):
          </p>
          <div className="space-y-1">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getFileIcon(file.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {customerTicketAttachmentService.formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  type="button"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading}
            type="button"
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {uploading ? 'Feltöltés...' : 'Fájlok feltöltése'}
          </button>
        </div>
      )}

      {/* Info */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Támogatott formátumok: Képek (JPG, PNG, GIF, WebP), PDF, Word, Excel, szöveges fájlok
      </p>
    </div>
  );
};

export default AttachmentUploader;
